import { ElementRef, ViewContainerRef } from "@angular/core";
import { Subscription } from "rxjs";
import { ContentControlStudioSettingsComponent } from "./content-control-studio-settings/content-control-studio-settings.component";
import { ETimelineMode, IContentControlStudioViewModel, IStudioEventsRef } from 'app/js/src/domain/content-control/studio';
import { IContentControlStudioTimeline } from './content-control-studio-timeline/i-content-control-studio-timeline';
import { ContentControlCanvasService } from "app/js/src/infrastructure/content-control/content-control-canvas.service";
import { environment } from "app/environments/environment";
import {
  ContentControlledEventActionEnum,
} from 'app/js/src/domain/content-control/enums';
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";
import {
  ContentControlledEditingDirective,
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';

import { IGetVideoCallbacksContext } from 'app/js/src/domain/content-control/studio';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { IStudioViewModelContext } from "./helpers/i-studio-view-model-context";
import { EffectAreaInteractionHandler } from "./helpers/effect-area-interaction-handler";
import { StudioPlaybackController } from "./helpers/studio-playback-controller";
import { StudioTimelineZoomManager } from "./helpers/studio-timeline-zoom-manager";
import { StudioAudioManager } from "./helpers/studio-audio-manager";
import { StudioSegmentController } from "./helpers/studio-segment-controller";
import { StudioActionButtonDispatcher } from "./helpers/studio-action-button-dispatcher";
import { StudioEventCoordinator } from "./helpers/studio-event-coordinator";
import { StudioRenderLoop } from "./helpers/studio-render-loop";
import { StudioKeyboardActions } from "./content-control-studio-keyboard-actions";

export abstract class ContentControlStudioBaseViewModel
  extends IContentControlStudioViewModel
  implements IStudioViewModelContext {
  // --- Dependencies ---
  protected _manager: IContentControlManager;
  protected abstract _getVideoSource(activeEvents?: ContentControlledEditingDirective[]): IContentControlVideoSource;
  protected abstract createAudioManager(): StudioAudioManager;
  protected _dialogService: IEvrideoDialogService;
  protected _viewContainerRef: ViewContainerRef;
  protected _eventsRef: IStudioEventsRef;
  protected _videoContainerElement: HTMLDivElement;
  protected _loadingText: HTMLElement;
  protected _mainVideoElement: IContentControlVideoSource;
  protected _canvasElementRef: ElementRef<HTMLCanvasElement>;
  protected _drawingCanvasElementRef: ElementRef<HTMLCanvasElement>;
  private _screenshotTakenSubscription: Subscription;

  // --- Helper instances ---
  protected _effectArea: EffectAreaInteractionHandler;
  protected _playback: StudioPlaybackController;
  protected _zoom: StudioTimelineZoomManager;
  protected _audio: StudioAudioManager;
  protected _segmentsController: StudioSegmentController;
  protected _actions: StudioActionButtonDispatcher;
  protected _events: StudioEventCoordinator;
  protected _renderLoop: StudioRenderLoop;

  public showDebugPanel = false;
  private _markForCheckFn: (() => void) | null = null;

  constructor(
    protected _canvasService: ContentControlCanvasService,
    protected _componentContext: IGetVideoCallbacksContext,
  ) {
    super();
  }

  // --------------------------------------------------------------------------
  // IStudioViewModelContext implementation
  // --------------------------------------------------------------------------

  markForCheck(): void {
    this._markForCheckFn?.();
  }

  getVideoSource(activeEvents?: ContentControlledEditingDirective[]): IContentControlVideoSource {
    return this._getVideoSource(activeEvents);
  }

  currentVideoTimeFn(): number {
    // Milliseconds — consumers feed this into getKeyframeByTime/isKeyframeTween,
    // whose keyframe times are in ms. Seconds here freeze effect rectangles at
    // the last keyframe because no interpolation window ever matches.
    return this.getVideoSource()?.currentPlayTimeAsClosestFrameMs();
  }

  abstract convertPlayerTimeToTimestampMs(playerTimeSecs: number): number;

  // --------------------------------------------------------------------------
  // Public state delegation — component accesses these via vm.*
  // --------------------------------------------------------------------------

  get keyframeChangeHolder(): ContentControlledKeyframe { return this._effectArea?.keyframeChangeHolder ?? null; }
  get isEffectAreaDragging(): boolean { return this._effectArea?.isEffectAreaDragging ?? false; }
  get isEffectAreaResizing(): boolean { return this._effectArea?.isEffectAreaResizing ?? false; }

  get isFitSegment(): boolean { return this._zoom?.isFitSegment ?? false; }
  get availableTimelineZoomSteps(): number[] { return this._zoom?.availableTimelineZoomSteps ?? []; }
  get timelineZoomSteps(): number[] { return this._zoom?.timelineZoomSteps ?? []; }
  get selectedTimelineZoomStepIndex(): number { return this._zoom?.selectedTimelineZoomStepIndex ?? 0; }
  get selectedTimelineBlockDuration(): number { return this._zoom?.selectedTimelineBlockDuration ?? 600; }

  get isVideoTimelineFit(): boolean {
    if (this._zoom?.isLiveZoomMode) return false;
    return (this.timelineZoomSteps.length || 1) - 1 === this.selectedTimelineZoomStepIndex;
  }

  get selectedSegmentId(): number | undefined { return this._manager?.selectedSegment?.id; }

  get isAudioMuted(): boolean { return this._playback.isAudioMuted; }
  get isAudioForcedMuted(): boolean { return this._playback.isAudioForcedMuted; }
  get selectedAudioOption(): string { return this._playback.selectedAudioOption; }
  set selectedAudioOption(val: string) { this._playback.selectedAudioOption = val; }
  get availableAudioTracks(): { [lang: string]: MediaElementAudioSourceNode } { return this._playback.availableAudioTracks; }
  set availableAudioTracks(val: { [lang: string]: MediaElementAudioSourceNode }) { this._playback.availableAudioTracks = val; }

  get segments(): ContentControlledEditingDirectiveViewModel[] { return this._segmentsController?.segments ?? []; }
  set segments(val: ContentControlledEditingDirectiveViewModel[]) { if (this._segmentsController) this._segmentsController.segments = val; }
  get multiEventsSelectedIds(): number[] { return this._segmentsController?.multiEventsSelectedIds ?? []; }
  set multiEventsSelectedIds(val: number[]) { if (this._segmentsController) this._segmentsController.multiEventsSelectedIds = val; }

  get subtitleOptions(): string[] { return this._audio?.subtitleOptions ?? []; }
  set subtitleOptions(val: string[]) { if (this._audio) this._audio.subtitleOptions = val; }
  get selectedSubtitleOptions(): string[] { return this._audio?.selectedSubtitleOptions ?? [IContentControlStudioViewModel.SK]; }
  set selectedSubtitleOptions(val: string[]) { if (this._audio) this._audio.selectedSubtitleOptions = val; }
  get audioOptions(): string[] { return this._audio?.audioOptions ?? []; }
  set audioOptions(val: string[]) { if (this._audio) this._audio.audioOptions = val; }

  get isVideoAudioTrack(): boolean { return this._playback.isVideoAudioTrack; }
  get isVideoPaused(): boolean { return this._playback.isVideoPaused; }
  get activeEvents(): ContentControlledEditingDirective[] { return this._renderLoop?.getActiveEvents() ?? []; }

  // --------------------------------------------------------------------------
  // Wiring
  // --------------------------------------------------------------------------

  setActionDependencies(
    manager: IContentControlManager,
    dialogService: IEvrideoDialogService,
    viewContainerRef: ViewContainerRef,
  ): void {
    this._manager = manager;
    this._dialogService = dialogService;
    this._viewContainerRef = viewContainerRef;

    this._screenshotTakenSubscription = this._manager.screenshotTaken.subscribe(
      (result) => this._events?.afterScreenshotTaken(result),
    );
  }

  setViewDependencies(
    eventsRef: IStudioEventsRef,
    videoContainer: HTMLDivElement,
    loadingText: HTMLElement,
    videoSource: IContentControlVideoSource,
    canvasElementRef: ElementRef<HTMLCanvasElement>,
    drawingCanvasElementRef: ElementRef<HTMLCanvasElement>,
    markForCheckFn: () => void,
  ): void {
    this._eventsRef = eventsRef;
    this._videoContainerElement = videoContainer;
    this._loadingText = loadingText;
    this._mainVideoElement = videoSource;
    this._canvasElementRef = canvasElementRef;
    this._drawingCanvasElementRef = drawingCanvasElementRef;
    this._markForCheckFn = markForCheckFn;

    // 1. Playback controller (no deps on other helpers)
    this._playback = new StudioPlaybackController(this, this._timelineRef);
    this._playback.setSetupAudioContextFn(() => this._audio?.initAudioContext());

    // 2. Audio manager
    this._audio = this.createAudioManager();

    // 3. Zoom manager (needs timeline, video, manager)
    this._zoom = new StudioTimelineZoomManager(
      this._timelineRef,
      this._getVideoSource(),
      this._manager,
      this,
    );

    // 4. Effect area handler (needs playback; segments wired later)
    this._effectArea = new EffectAreaInteractionHandler(
      this._canvasService,
      this._manager,
      this._timelineRef,
      this._drawingCanvasElementRef?.nativeElement,
      this,
      this._playback,
    );

    // 5. Segment controller (needs zoom, effectArea; events wired later)
    this._segmentsController = new StudioSegmentController(
      this._manager,
      this._canvasService,
      this._timelineRef,
      this._dialogService,
      this._viewContainerRef,
      this,
      this._zoom,
      this._effectArea,
      this._eventsRef,
    );

    // 6. Event coordinator (needs segments, effectArea, audio, zoom)
    this._events = new StudioEventCoordinator(
      this._manager,
      this._canvasService,
      this._timelineRef,
      this._dialogService,
      this._viewContainerRef,
      this,
      this._segmentsController,
      this._effectArea,
      this._audio,
      this._zoom,
    );

    // 7. Wire circular dependencies
    this._effectArea.setSegmentController(this._segmentsController);
    this._segmentsController.setEventCoordinator(this._events);

    // 8. Action button dispatcher
    this._actions = new StudioActionButtonDispatcher(
      this._manager,
      this._canvasService,
      this._timelineRef,
      this,
      this._events,
      this._segmentsController,
      this._audio,
      this._zoom,
    );

    // 9. Render loop
    this._renderLoop = new StudioRenderLoop(
      this._manager,
      this._canvasService,
      this._timelineRef,
      this,
      this._playback,
      this._segmentsController,
      this._effectArea,
      this._audio,
    );
  }

  public setupViewModel(
    manager: IContentControlManager,
    dialogService: IEvrideoDialogService,
    viewContainerRef: ViewContainerRef,
    timelineRef: IContentControlStudioTimeline,
    eventsRef: IStudioEventsRef,
    playerElement: HTMLDivElement,
    loadingText: HTMLElement,
    videoSource: IContentControlVideoSource,
    canvasElementRef: ElementRef<HTMLCanvasElement>,
    drawingCanvasElementRef: ElementRef<HTMLCanvasElement>,
    detectChangesFn: () => void,
  ): void {
    this.setActionDependencies(manager, dialogService, viewContainerRef);
    this.setTimelineRef(timelineRef);
    this.setViewDependencies(
      eventsRef, playerElement, loadingText,
      videoSource, canvasElementRef, drawingCanvasElementRef,
      detectChangesFn,
    );
    this.setKeyboardActions(new StudioKeyboardActions(
      () => videoSource,
      manager, dialogService, viewContainerRef,
      this,
    ));
    this.setupVideo();
  }

  // --------------------------------------------------------------------------
  // Playback delegation
  // --------------------------------------------------------------------------

  play(): void { this._playback.play(); }
  pause(): void { this._playback.pause(); }
  changePlaybackSpeed(change: number): void { this._playback.changePlaybackSpeed(change); }
  frameUp(multiply: number = 1): void { this._playback.frameUp(multiply); }
  frameDown(multiply: number = 1): void { this._playback.frameDown(multiply); }
  mute(event: Event): boolean { return this._playback.mute(event); }
  unmute(event: Event): boolean { return this._playback.unmute(event); }
  toggleMute(force: boolean = false): void { this._playback.toggleMute(force); }
  toggleMuteKey(): void { this._playback.toggleMuteKey(); }

  // --------------------------------------------------------------------------
  // Timeline zoom delegation
  // --------------------------------------------------------------------------

  timelineZoom(change: number): void { this._zoom.timelineZoom(change); }
  timelineZoomFitVideo(): void { this._zoom.timelineZoomFitVideo(); }
  timelineZoomFitSegment(goTo: "in" | "out" = "in"): void { this._zoom.timelineZoomFitSegment(goTo); }
  goToIn(): void { this._zoom.goToIn(); }
  goToOut(): void { this._zoom.goToOut(); }
  calculateZoomLevels(time: number): void { this._zoom.calculateZoomLevels(time); }

  // --------------------------------------------------------------------------
  // Segment controller delegation
  // --------------------------------------------------------------------------

  changeSelectedSegment(id: number, isMulti: boolean = false): void { this._segmentsController.changeSelectedSegment(id, isMulti); }
  chooseWhatToDelete(): void { this._segmentsController.chooseWhatToDelete(); }
  async markIn(): Promise<void> { await this._segmentsController.markIn(); }
  async markOut(): Promise<void> { await this._segmentsController.markOut(); }
  getActiveSegment(): ContentControlledEditingDirectiveViewModel { return this._segmentsController.getActiveSegment(); }
  eventsChangeDetected(): void { this._segmentsController.eventsChangeDetected(); }
  onRevertEventClicked(id: number): void { this._segmentsController.onRevertEventClicked(id); }
  onMultiEventsClicked(ids: number[]): void { this._segmentsController.onMultiEventsClicked(ids); }
  async activeSegmentChangedOnTimeline(timelineSegment: ContentControlledEditingDirectiveViewModel): Promise<void> {
    await this._segmentsController.activeSegmentChangedOnTimeline(
      timelineSegment,
      async (seg) => { await this._events.segmentToEvent(seg); },
    );
  }

  // --------------------------------------------------------------------------
  // Action button delegation
  // --------------------------------------------------------------------------

  async actionButtonClicked(eventType: number): Promise<void> { await this._actions.actionButtonClicked(eventType); }

  // --------------------------------------------------------------------------
  // Event coordinator delegation
  // --------------------------------------------------------------------------

  async segmentToEvent(
    segment: ContentControlledEditingDirectiveViewModel,
    action?: ContentControlledEventActionEnum,
    keyframe?: ContentControlledKeyframe,
    doPostActions: boolean = true,
  ): Promise<boolean> {
    return this._events.segmentToEvent(segment, action, keyframe, doPostActions);
  }

  openSubtitleEditorDialog(segment: ContentControlledEditingDirectiveViewModel): void {
    this._events.openSubtitleEditorDialog(segment);
  }

  // --------------------------------------------------------------------------
  // Audio manager delegation
  // --------------------------------------------------------------------------

  initAudioContext(): void { this._audio?.initAudioContext(); }
  initAudioTracks(): void { this._audio?.initAudioTracks(); }
  onAudioChange(option: string): void { this._audio?.onAudioChange(option); }
  syncAudio(): void { this._audio?.syncAudio(); }
  onSubtitleChange(options: string[]): void { this._audio?.onSubtitleChange(options); }

  // --------------------------------------------------------------------------
  // Remaining methods that stay in the base class
  // --------------------------------------------------------------------------

  onTimelineModeChange(mode: string): void {
    if (mode === 'OT') {
      this.timelineMode = ETimelineMode.OriginalTime;
    } else if (mode === 'ET') {
      this.timelineMode = ETimelineMode.EditedTime;
    }

    if (
      this._manager.selectedSegment &&
      this._manager.selectedSegment.action === ContentControlledEventActionEnum.Cut
    ) {
      this._keyboardActions.changeSelectedSegment(-1);
    }
    setTimeout(() => {
      this._zoom.calculateZoomLevels(this._timelineRef.currentVideoTimeSecs(this._timelineRef.isFitSegment, this._timelineRef.isEditedTimeline));
      this.markForCheck();
      this._timelineRef.onChangeEffectingTimeline(true);
    }, 0);
  }

  onStudioKeyDown(event: KeyboardEvent): void {
    if (this.isFragmentDialogOpen) return;
    if (event.target && event.target instanceof HTMLInputElement) return;
    if (environment.production && event.code !== "F12") {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (event.code === "Space" || event.code === "KeyK") {
      if (this._keyboardActions.isVideoPaused()) {
        this._keyboardActions.play();
      } else {
        this._keyboardActions.pause();
      }
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      if (event.shiftKey && event.code === "KeyZ") { this._keyboardActions.redo(); return; }
      if (event.code === "KeyZ") { this._keyboardActions.undo(); return; }
      if (event.code === "KeyS") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this._keyboardActions.save();
        return;
      }
      if (
        (event.shiftKey && event.code === "Slash") ||
        (event.altKey && event.code === "KeyK")
      ) {
        this._keyboardActions.openKeyboardShortcutsDialog();
      }
    }
    if (event.shiftKey && event.code === "ArrowUp") { this.supportsPlaybackSpeed() && this._keyboardActions.changePlaybackSpeed(0.1); return; }
    if (event.shiftKey && event.code === "ArrowDown") { this.supportsPlaybackSpeed() && this._keyboardActions.changePlaybackSpeed(-0.1); return; }
    if (event.shiftKey && event.code === "Backspace") { this.supportsPlaybackSpeed() && this._keyboardActions.changePlaybackSpeed(0); return; }
    if (event.shiftKey && event.code === "ArrowRight") { this._keyboardActions.frameUp(5); return; }
    if (event.code === "PageUp") { this._timelineRef.goToKeyframe("next"); return; }
    if (event.code === "PageDown") { this._timelineRef.goToKeyframe("previous"); return; }
    if (event.code === "ArrowRight") { this._keyboardActions.frameUp(); return; }
    if (event.shiftKey && event.code === "ArrowLeft") { this._keyboardActions.frameDown(5); return; }
    if (event.shiftKey && (event.code === "Digit1" || event.code === "ExclamationMark")) { this._keyboardActions.timelineZoomFitVideo(); return; }
    if (event.shiftKey && (event.code === "Digit2" || event.code === "AtSign")) { this._keyboardActions.timelineZoomFitSegment(); return; }
    if (event.shiftKey && event.code === "KeyI") { this._keyboardActions.goToIn(); return; }
    if (event.shiftKey && event.code === "KeyO") { this._keyboardActions.goToOut(); return; }
    if (event.code === "ArrowLeft") { this._keyboardActions.frameDown(); return; }
    if (event.code === "Escape") { this._keyboardActions.changeSelectedSegment(-1); return; }
    if (event.code === "Delete") { this._keyboardActions.chooseWhatToDelete(); return; }
    if (event.code === "KeyM") { this._keyboardActions.toggleMuteKey(); return; }
    if (event.code === "KeyI") { this._keyboardActions.markIn(); return; }
    if (event.code === "KeyO") { this._keyboardActions.markOut(); return; }
    if (event.code === "Equal") { this._keyboardActions.timelineZoom(-1); return; }
    if (event.code === "Minus") { this._keyboardActions.timelineZoom(1); return; }
  }

  onWindowResize(): void {
    this.resizeVideo();
    this._canvasService.postResizeActions();
    this.timelineRef.setPlayheadHeight();
    this.timelineRef.onChangeEffectingTimeline(true);
  }

  save(): void {
    this._dialogService.openDialogWithLoading(
      this._viewContainerRef,
      { title: "Save", loadingContent: "Saving changes...." },
      this._manager.save(),
      (_: string) => ({
        type: "success",
        title: "Save",
        content: "item saved successfuly",
        timeout: 1000,
      }),
    );
  }

  openSettingsDialog(): void {
    this.isFragmentDialogOpen = true;
    const tempSettings = this._manager.itemSettings.clone();
    this._dialogService
      .openDialog(this._viewContainerRef, {
        title: `${this._manager.mediaName} - Settings`,
        type: "simple",
        cancelButtonText: "cancel",
        confirmButtonText: "save",
        content: ContentControlStudioSettingsComponent,
        contentContext: { settings: this._manager.itemSettings },
        timeout: -1,
      })
      .then((result) => {
        if (result) {
          this._manager.updateSettings = tempSettings;
          this._canvasService.updateScalingFactor(tempSettings.aiDetections.scalingFactor);
        }
      })
      .finally(() => (this.isFragmentDialogOpen = false));
  }

  clearDrags(event: MouseEvent): void {
    this._timelineRef.clearDrags(event);
  }

  resizeVideo(): void {
    if (!this._mainVideoElement || !this._canvasElementRef || !this._drawingCanvasElementRef) return;
    const video = this._mainVideoElement;
    const canvas = this._canvasElementRef.nativeElement;
    const drawingCanvas = this._drawingCanvasElementRef.nativeElement;
    const container = canvas.parentElement as HTMLElement;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    let width = containerWidth;
    let height = width * (9 / 16);
    if (height > containerHeight) {
      height = containerHeight;
      width = height * (16 / 9);
    }
    video.widthPx = drawingCanvas.width = canvas.width = width;
    video.heightPx = drawingCanvas.height = canvas.height = height;
  }

  onVideoLoadedEventHandler(): void {

      this.audioOptions = this.setupAudio(this.audioOptions);
      document.addEventListener("click", this._audio.setupAudioFunctionHandler);
      this.subtitleOptions = this.setupSubtitles(this.subtitleOptions);
      this._zoom.calculateZoomLevels(this._timelineRef.currentVideoTimeSecs(this._timelineRef.isFitSegment, this._timelineRef.isEditedTimeline));
      this._manager.editingDirectives.forEach((ed: ContentControlledEditingDirective) => {
        const sv = ed.toView(0);
        this._segmentsController.segments.push(sv);
      });
      this._loadingText.style.display = "none";
      this._videoContainerElement.style.display = "flex";
      setTimeout(() => {
        this._timelineRef.onVideoMetadataLoaded();
        this.resizeVideo();
        this._canvasService.initWebGLService(
          this._mainVideoElement,
          this._canvasElementRef,
          this._drawingCanvasElementRef,
          this._timelineRef.oneFrameSecs,
        );
        this._canvasService.updateScalingFactor(this._manager.itemSettings.aiDetections.scalingFactor);
        this._drawingCanvasElementRef.nativeElement.addEventListener(
          "mousedown", this._effectArea.onMouseDown.bind(this._effectArea),
        );
        this._drawingCanvasElementRef.nativeElement.addEventListener(
          "mousemove", this._effectArea.onMouseMove.bind(this._effectArea),
        );
        this._drawingCanvasElementRef.nativeElement.addEventListener(
          "mouseup", this._effectArea.onMouseUp.bind(this._effectArea),
        );
        this._drawingCanvasElementRef.nativeElement.addEventListener(
          "mouseleave", this._effectArea.onMouseUp.bind(this._effectArea),
        );
        this.onVideoLoaded(this._getVideoSource());
        this._canvasService.startRender();
        this._renderLoop.start();
      }, 0);

  }

  async onHeaderButtonClicked(button: string): Promise<void> {
    switch (button) {
      case "render": {
        if (this._manager.hasChanges) {
          await this._dialogService.openDialogWithLoading(
            this._viewContainerRef,
            { title: "Save", loadingContent: "Saving changes...." },
            this._manager.save(),
            undefined,
          );
        }
        const result = await this._manager.renderItems([]);
        if (result.success.length > 0) {
          this._dialogService
            .openDialog(this._viewContainerRef, {
              type: "success",
              title: "render & exit",
              content: result.success.join("\n"),
              cancelButtonText: "Stay in studio",
              confirmButtonText: "Exit",
            })
            .then((r) => { if (r) close(); });
        } else if (result.errors.length > 0) {
          this._dialogService.openDialog(this._viewContainerRef, {
            type: "error",
            title: "render & exit",
            content: result.errors.join("\n"),
            timeout: 5000,
          });
        }
        break;
      }
      case "save": {
        this.save();
        break;
      }
      case "clear": {
        const clearCommands = () => {
          this._manager.selectedSegment = null;
          this._segmentsController.segments = [];
          this._manager.clear();
        };
        if (this._manager.hasChanges) {
          this._dialogService
            .openDialog(this._viewContainerRef, {
              type: "warning",
              title: "Unsaved changes",
              content: "You have unsaved changes, do you wish to continue?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
            })
            .then((r) => { if (r) clearCommands(); });
        } else {
          clearCommands();
        }
        break;
      }
      case "undo":    { this._manager.undoOperation(); break; }
      case "redo":    { this._manager.redoOperation(); break; }
      case "settings":{ this.openSettingsDialog(); break; }
      default: break;
    }
  }
  abstract timeFormatFunction(milliseconds: number, justMinutesAndSeconds?: boolean): string;
  destroy(): void {
    // Stop both rAF loops - the canvas service and manager are module-level
    // singletons, so a loop leaked here keeps rendering into the next studio
    // instance with stale state (frozen effect rectangles).
    this._renderLoop?.stop();
    this._canvasService.stopRender();
    if (this._audio?.setupAudioFunctionHandler) {
      document.removeEventListener("click", this._audio.setupAudioFunctionHandler);
    }
    this._screenshotTakenSubscription?.unsubscribe();
  }
}
